<style lang="scss">
    .sunburst {
        position: relative;
    }
    svg {
        width: 100%;
        height: auto;
        display: block;
    }

    .dimmed {
        opacity: .1 !important;
    }

    .legend-label {
        font-size: toRem(12px);
    }
</style>
<template>
    <div class="sunburst">
        <tooltip v-bind="tooltip" />
        <div class="sunburst__visualization"></div>
    </div>
</template>
<script>
import {
    select,
    partition,
    scaleLinear,
    scaleSqrt,
    arc,
    hierarchy,
    // interpolate,
    selectAll,
    event,
} from 'd3'
const Tooltip = () => import(/* webpackChunkName: 'Tooltip' */ '@/components/Tooltip')

export default {
    name: 'Sunburst',
    props: {
        hierarchy: {
            type: Object,
            required: true,
        },
        top: {
            type: Number,
            default: 20,
        },
        right: {
            type: Number,
            default: 10,
        },
        bottom: {
            type: Number,
            default: 40,
        },
        left: {
            type: Number,
            default: 210,
        },
        width: {
            type: Number,
            default: 645,
        },
        height: {
            type: Number,
            default: 645,
        },
    },
    data: () => ({
        tooltip: {
            active: false,
            text: '',
            top: 0,
            left: 0,
        },
    }),
    computed: {
        sunburst() {
            return select(this.$el.querySelector('.sunburst__visualization'))
                .append('svg')
                .attr('viewBox', `0 0 ${this.dimensions.width + this.dimensions.left + this.dimensions.right} ${this.dimensions.height + this.dimensions.top + this.dimensions.bottom}`)
                .append('g')
                .attr('transform', `translate(${this.dimensions.left}, ${this.dimensions.top})`)
        },
        sunburstVis() {
            return this.sunburst
                .attr('width', this.dimensions.width + this.dimensions.left + this.dimensions.right)
                .attr('height', this.dimensions.height + this.dimensions.top + this.dimensions.bottom)
                .append('g')
                .attr('transform', `translate(${this.dimensions.width / 2}, ${this.dimensions.height / 2})`)
        },
        partition() {
            return partition()
        },
        sunburstX() {
            return scaleLinear().range([0, 2 * Math.PI])
        },
        sunburstY() {
            return scaleSqrt().range([0, this.dimensions.radius])
        },
        arc() {
            return arc()
                .startAngle(d => Math.max(0, Math.min(2 * Math.PI, this.sunburstX(d.x0))))
                .endAngle(d => Math.max(0, Math.min(2 * Math.PI, this.sunburstX(d.x1))))
                .innerRadius(d => Math.max(0, this.sunburstY(d.y0)))
                .outerRadius(d => Math.max(0, this.sunburstY(d.y1)))
        },
        root() {
            return hierarchy(this.hierarchy)
                .sum(d => d.size)
                .each(d => {
                    d.data.selected = false
                })
        },
        finalLegend() {
            return this.getChildren(this.root.children)
        },
        legendBoxHeight() {
            return this.dimensions.height / this.root.descendants().length
        },
        dimensions() {
            let width = this.width - this.left - this.right
            let height = this.height - this.top - this.bottom
            return {
                top: this.top,
                right: this.right,
                bottom: this.bottom,
                left: this.left,
                width,
                height,
                radius: Math.min(width, height) / 2,
            }
        },
    },
    methods: {
        click(d) {
            // this.sunburst.transition()
            //     .duration(750)
            //     .tween('scale', () => {
            //         let xd = interpolate(this.sunburstX.domain(), [d.x0, d.x1])

            //         let yd = interpolate(this.sunburstY.domain(), [d.y0, 1])

            //         let yr = interpolate(this.sunburstY.range(), [d.y0 ? 20 : 0, this.dimensions.radius])
            //         return t => {
            //             this.sunburstX.domain(xd(t))
            //             this.sunburstY.domain(yd(t)).range(yr(t))
            //         }
            //     })
            //     .selectAll('path')
            //     .attrTween('d', d => () => this.arc(d))
            let ancestors = d.ancestors()
            if (ancestors.length > 1) {
                this.$emit('click', ancestors[ancestors.length - 2].data.key)
            }
        },
        highlightRelatedNodes(node) {
            selectAll('.letiant').classed('dimmed', true)
            node.each(d => selectAll(`.${d.parent ? `${d.parent.data.key}-` : ''}${d.data.key}`).classed('dimmed', false))
        },
        getChildren(nodes) {
            return nodes.reduce((sum, n) => [...sum, n, ...(n.children ? this.getChildren(n.children) : [])], [])
        },
    },
    mounted() {
        this.sunburstVis.selectAll('path')
            .data(this.partition(this.root).descendants())
            .enter()
            .append('path')
            .attr('d', this.arc)
            .attr('class', d => `letiant ${d.parent ? `${d.parent.data.key}-` : ''}${d.data.key}`)
            .style('stroke', '#FFF')
            .style('fill', d => d.data.color)
            .on('click', this.click)
            .on('mouseenter', d => {
                let current = select(event.currentTarget)
                current.style('cursor', 'pointer')

                if (d.data.name === 'root') {
                    return null
                }

                current.datum().data.selected = true

                let offset = this.$el.querySelector('svg').getBoundingClientRect()
                this.tooltip = {
                    active: true,
                    text: current.datum().data.name,
                    top: event.pageY - offset.top - window.pageYOffset,
                    left: event.pageX - offset.left - window.pageXOffset,
                }
                this.highlightRelatedNodes(d)
            })
            .on('mouseout', () => {
                let current = select(event.currentTarget)
                current.style('cursor', 'default')
                this.tooltip.active = false
                current.datum().data.selected = false
                selectAll('.letiant').classed('dimmed', false)
            })

        let legend = this.sunburst.selectAll('.legend')
            .data(this.finalLegend)
            .enter()
            .append('g')
            .classed('legend', true)
            .attr('class', d => `letiant ${d.parent ? `${d.parent.data.key}-` : ''}${d.data.key}`)
            .attr('transform', (d, i) => `translate(-80, ${i * this.legendBoxHeight})`)
            .on('mouseenter', d => {
                let current = select(event.currentTarget)
                current.datum().data.selected = true
                this.highlightRelatedNodes(d)
            })
            .on('mouseleave', () => {
                select(event.currentTarget).datum().data.selected = false
                selectAll('.letiant').classed('dimmed', false)
            })
            .on('click', this.click)

        legend.append('rect')
            .attr('x', 0)
            .attr('width', 15)
            .attr('height', this.legendBoxHeight)
            .style('fill', d => d.data.color)

        legend.append('text')
            .attr('x', 0)
            .attr('dy', 10)
            .attr('class', 'legend-label')
            .attr('dx', -10)
            .attr('text-anchor', 'end')
            .style('fill', d => d.data.color)
            .text(d => d.data.name)

    },
    components: {
        Tooltip,
    },
}
</script>
